import { Box, Typography } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

import { StatusChip } from './atoms';
import { NodeResult } from './types';

const SubEngineTooltip: React.FC<{ node: NodeResult }> = ({ node }) => (
  <Box sx={{ p: 2, maxWidth: 360, minWidth: 300 }}>
    <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}
      >
        {node.nodeName || 'Nodo de Decisión'}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', fontSize: '0.8rem' }}
      >
        Este nodo contiene {node.subDecisionEngineRevisionStory!.length}{' '}
        sub-nodos
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {node.subDecisionEngineRevisionStory!.map((subNode, i) => (
        <Box
          key={i}
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 1.5,
            border: '1px solid #e0e0e0',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              left: 12,
              backgroundColor: BASE_COLORS.blue,
              color: BASE_COLORS.white,
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.65rem',
              fontWeight: 700,
            }}
          >
            {i + 1}
          </Box>
          <Box sx={{ mt: 1 }}>
            {subNode.nodeName && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}
              >
                {subNode.nodeName}
              </Typography>
            )}
            {subNode.resultText && (
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1 }}
              >
                {subNode.resultText}
              </Typography>
            )}
            {subNode.statusNode && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                >
                  Decisión:
                </Typography>
                <StatusChip
                  label={subNode.statusNode.name}
                  severity={subNode.statusNode.severity}
                />
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const SimpleNodeTooltip: React.FC<{ node: NodeResult }> = ({ node }) => {
  const isDecision =
    node.statusNode?.severity !== undefined &&
    node.statusNode?.severity !== null;

  return (
    <Box sx={{ p: 2, maxWidth: 360, minWidth: 280 }}>
      <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}
        >
          {node.nodeName || 'Nodo de Evaluación'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {isDecision ? 'Nodo de Decisión' : 'Nodo de Proceso'}
        </Typography>
      </Box>
      {node.resultText && (
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            p: 1.5,
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', lineHeight: 1.5, fontWeight: 500 }}
          >
            {node.resultText}
          </Typography>
        </Box>
      )}
      {node.statusNode && (
        <Box
          sx={{
            backgroundColor: '#e3f2fd',
            p: 1.5,
            borderRadius: 2,
            border: '1px solid #90caf9',
          }}
        >
          {isDecision ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Decisión
              </Typography>
              <StatusChip
                label={node.statusNode.name}
                severity={node.statusNode.severity}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Siguiente paso
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'white',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.8rem',
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                >
                  {node.statusNode.name}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const NodeTooltipContent: React.FC<{ node: NodeResult }> = ({ node }) => {
  const hasSubs =
    node.subDecisionEngineRevisionStory &&
    node.subDecisionEngineRevisionStory.length > 0;

  return hasSubs ? (
    <SubEngineTooltip node={node} />
  ) : (
    <SimpleNodeTooltip node={node} />
  );
};

export default NodeTooltipContent;
